import type { ScenarioScreenshot } from '../archive/scenarioArchiveBuilder';
import type { RecordingMode } from './scenarioRecorder';

/**
 * Port for capturing the current web view as PNG bytes. The infra adapter
 * ({@link ../recording/html2canvasCapturer}) renders the DOM; tests inject a
 * fake. Keeping this a one-method port makes the collector below fully
 * unit-testable without a DOM or html2canvas.
 */
export interface ScreenshotCapturer {
  /** Capture the current web view as PNG bytes. Rejects when capture is impossible. */
  capture(): Promise<Uint8Array>;
}

/** Archive ref of the final-state screenshot taken when a recording stops. */
export const FINAL_STATE_SCREENSHOT_REF = 'screenshots/final-state.png';

/** Stable, sortable archive ref for a per-step screenshot. */
export function stepScreenshotRef(index: number): string {
  return `screenshots/step-${String(index).padStart(3, '0')}.png`;
}

/**
 * Key steps worth a screenshot (scope §26 Q7 — key steps + final-state, not
 * every step): the command that drives a Revit action, its result, and any UI
 * error. Mirrors {@link ScenarioRecorder.recordCommand}/`recordActionResult`/
 * `recordUiError` action names.
 */
export function isKeyScreenshotAction(action: string): boolean {
  return action.startsWith('command.') || action === 'action.result' || action === 'ui.error';
}

export interface ScreenshotCollectorOptions {
  /**
   * Best-effort failure sink (card E). A capture that throws or yields empty
   * bytes is reported here and dropped — never re-thrown, never blocks recording.
   * Defaults to a no-op.
   */
  onWarning?: (message: string) => void;
}

/**
 * Accumulates key-step + final-state screenshots for the archive, **Full mode
 * only** (Lightweight records no screenshots). Depends solely on the
 * {@link ScreenshotCapturer} port, so it is unit-testable with a fake capturer.
 *
 * Drive it live as steps are recorded: {@link captureStep} on each recorded
 * step, {@link captureFinalState} once on stop. The archive builder then reads
 * {@link getScreenshots} (the PNG files) and {@link getStepRefs} (the step→ref
 * map that lands as `step.screenshotRef`).
 */
export class ScreenshotCollector {
  private readonly capturer: ScreenshotCapturer;
  private mode: RecordingMode;
  private readonly onWarning: (message: string) => void;
  private readonly shots = new Map<string, Uint8Array>();
  private readonly stepRefs: Record<number, string> = {};

  constructor(capturer: ScreenshotCapturer, mode: RecordingMode = 'full', options: ScreenshotCollectorOptions = {}) {
    this.capturer = capturer;
    this.mode = mode;
    this.onWarning = options.onWarning ?? (() => {});
  }

  /** Track the active recording mode (screenshots are captured in Full only). */
  setMode(mode: RecordingMode): void {
    this.mode = mode;
  }

  /**
   * Capture a screenshot for one recorded step, if it is a key step in Full
   * mode. Returns the assigned ref, or null when skipped or capture failed.
   */
  async captureStep(index: number, action: string): Promise<string | null> {
    if (this.mode !== 'full' || !isKeyScreenshotAction(action)) {
      return null;
    }
    const ref = stepScreenshotRef(index);
    const captured = await this.tryCapture(ref, `step ${index} (${action})`);
    if (!captured) {
      return null;
    }
    this.stepRefs[index] = ref;
    return ref;
  }

  /** Capture the final-state screenshot (Full mode only). Returns its ref or null. */
  async captureFinalState(): Promise<string | null> {
    if (this.mode !== 'full') {
      return null;
    }
    const captured = await this.tryCapture(FINAL_STATE_SCREENSHOT_REF, 'final state');
    return captured ? FINAL_STATE_SCREENSHOT_REF : null;
  }

  /** Every captured screenshot as a builder-ready artifact list (sorted by ref). */
  getScreenshots(): ScenarioScreenshot[] {
    return [...this.shots.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([ref, data]) => ({ ref, data }));
  }

  /** The step-index → screenshot-ref map the builder folds into `step.screenshotRef`. */
  getStepRefs(): Record<number, string> {
    return { ...this.stepRefs };
  }

  /** Drop everything (start/reset of a fresh recording). */
  reset(): void {
    this.shots.clear();
    for (const key of Object.keys(this.stepRefs)) {
      delete this.stepRefs[Number(key)];
    }
  }

  private async tryCapture(ref: string, label: string): Promise<boolean> {
    try {
      const data = await this.capturer.capture();
      if (!data || data.length === 0) {
        throw new Error('empty capture');
      }
      this.shots.set(ref, data);
      return true;
    } catch {
      this.onWarning(`Screenshot capture failed for ${label}.`);
      return false;
    }
  }
}
